import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trophy, Flame, ChevronRight, MapPin, CheckCircle2, Loader2, GraduationCap, BookOpen, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_ICON = { class: GraduationCap, library: BookOpen, rec: Dumbbell };

const getPos = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve([p.coords.latitude, p.coords.longitude]),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

const mmss = (mins) => {
  const t = Math.max(0, Math.round(mins * 60));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

// Compact rewards tracker + one-tap check-in, pinned to the top of the homepage.
export default function RewardTracker() {
  const [me, setMe] = useState(null);
  const [active, setActive] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [nearby, setNearby] = useState([]);
  const [busy, setBusy] = useState("");
  const tick = useRef(null);

  const loadMe = useCallback(() => api.get("/gamification/me").then((r) => setMe(r.data)).catch(() => {}), []);

  const loadNearby = useCallback(async (l) => {
    try {
      const r = await api.get(`/checkins/nearby?lat=${l[0]}&lng=${l[1]}`);
      const flat = [];
      ["class", "library", "rec"].forEach((k) =>
        (r.data.places[k] || []).forEach((p) => flat.push({ ...p, kind: k })));
      flat.sort((a, b) => a.distance_m - b.distance_m);
      setNearby(flat.slice(0, 3));
    } catch { setNearby([]); }
  }, []);

  useEffect(() => {
    loadMe();
    api.get("/checkins/active").then((r) => {
      setActive(r.data.active);
      if (r.data.active) setElapsed(r.data.active.elapsed_min);
    }).catch(() => {});
    getPos().then((l) => loadNearby(l)).catch(() => setNearby([]));
  }, [loadMe, loadNearby]);

  useEffect(() => {
    if (!active) { clearInterval(tick.current); return; }
    const started = new Date(active.started_at).getTime();
    tick.current = setInterval(() => setElapsed((Date.now() - started) / 60000), 1000);
    return () => clearInterval(tick.current);
  }, [active]);

  const ready = active && elapsed >= (active.min_minutes || 30);

  const startCheckin = async (p) => {
    setBusy(p.id);
    try {
      const l = await getPos();
      const r = await api.post("/checkins/start", { kind: p.kind, place_id: p.id, lat: l[0], lng: l[1] });
      setActive(r.data); setElapsed(0);
      toast.success(`Checked in at ${p.name}. Stay ${r.data.min_minutes} min to earn ${r.data.points_preview} pts.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check in."); }
    finally { setBusy(""); }
  };

  const checkout = async () => {
    if (!active) return;
    setBusy("checkout");
    try {
      const l = await getPos();
      const r = await api.post(`/checkins/${active.id}/complete`, { lat: l[0], lng: l[1] });
      if (r.data.awarded) {
        toast.success(`+${r.data.points} pts for ${r.data.place_name}!`);
        if (r.data.streak_bonus > 0) toast.success(`🔥 +${r.data.streak_bonus} streak bonus!`);
        (r.data.new_badges || []).forEach((b) => toast.success(`🏅 New badge: ${b.name}!`, { duration: 6000 }));
        setActive(null);
        await Promise.all([loadMe(), loadNearby(l)]);
      } else {
        toast.message(`Stay ${r.data.remaining_min} more min to earn points.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check out."); }
    finally { setBusy(""); }
  };

  if (!me) return null;
  const lvl = me.level || {};
  const pct = Math.round((lvl.progress || 0) * 100);

  return (
    <div className="rounded-xl bg-brand text-white px-4 py-3 mb-4" data-testid="home-reward-tracker">
      <Link to="/rewards" className="flex items-center gap-3 hover:opacity-95 transition" data-testid="home-reward-link">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Trophy size={18} strokeWidth={1.7} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg font-semibold leading-none" data-testid="home-reward-points">
              {me.points} pts
            </span>
            <span className="mono text-[11px] uppercase tracking-widest opacity-80">Lvl {lvl.level} · {lvl.title}</span>
            {me.streak > 0 && (
              <span className="flex items-center gap-1 text-[11px] font-bold bg-white/15 rounded-full px-2 py-0.5" data-testid="home-reward-streak">
                <Flame size={12} /> {me.streak}d streak
              </span>
            )}
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-white/25 overflow-hidden max-w-[280px]">
            <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <ChevronRight size={18} className="opacity-70 shrink-0" />
      </Link>

      {/* One-tap check-in */}
      {active ? (
        <div className="mt-3 pt-3 border-t border-white/20 flex items-center gap-2 flex-wrap" data-testid="home-active-checkin">
          <span className="flex items-center gap-1.5 text-sm min-w-0">
            <MapPin size={14} className="shrink-0" />
            <span className="truncate">{active.place_name}</span>
          </span>
          <span className="mono text-sm font-bold ml-auto" data-testid="home-checkin-timer">
            {ready ? "Ready!" : mmss(elapsed)}
          </span>
          <button onClick={checkout} disabled={!ready || busy === "checkout"} data-testid="home-checkout-btn"
            className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition
              ${ready ? "bg-white text-brand hover:opacity-90" : "bg-white/20 text-white/70 cursor-not-allowed"}`}>
            {busy === "checkout" ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />}
            {ready ? "Check out" : "Stay put"}
          </button>
        </div>
      ) : nearby.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-white/20" data-testid="home-checkin-nearby">
          <div className="mono text-[11px] uppercase tracking-widest opacity-80 mb-1.5">Check in nearby · one tap</div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {nearby.map((p) => {
              const I = KIND_ICON[p.kind];
              return (
                <button key={p.id} onClick={() => startCheckin(p)} disabled={busy === p.id}
                  data-testid={`home-checkin-${p.kind}-${p.id}`}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-medium whitespace-nowrap transition">
                  {busy === p.id ? <Loader2 className="animate-spin" size={13} /> : <I size={13} />}
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
